/**
 * ComplianceView — contribution limits + expenditure ledger + filing export (M7).
 *
 * Limits are stored as DATA (office → limit, null = unlimited), not hardcoded
 * jurisdiction logic. Expenditures carry an auto-generated "Paid for by …"
 * disclaimer and export to a filing CSV (audited server-side).
 *
 * Reads are open to admin/org-manager/manager/campaign-manager; writes (rules,
 * expenditures) are admin/org-manager only — the server enforces both, this UI
 * just hides the forms for non-writers.
 *
 * Pattern mirrors LeadsView / MyVolunteersView: axios + useState/useEffect,
 * semantic Tailwind tokens, ViewHeader, useIsMobile. Strictly typed.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Scale, ShieldAlert, Download } from 'lucide-react';
import { ViewHeader } from '@/components/ViewHeader';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { triggerBrowserDownload } from '@/api/exports';
import {
  listRules,
  upsertRule,
  listExpenditures,
  createExpenditure,
  getDisclaimer,
  exportExpendituresCsv,
  type ContributionRule,
  type Expenditure,
} from '@/api/compliance';
import { roleMatches } from '@/lib/roles';

const VIEW_ROLES = ['ADMIN', 'ORG_MANAGER', 'MANAGER', 'SMS_MANAGER'];
const WRITE_ROLES = ['ADMIN', 'ORG_MANAGER'];

function money(cents: number | null | undefined): string {
  if (cents == null) return 'No limit';
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0 })}`;
}
function dollarsToCents(v: string): number {
  const n = Number.parseFloat(v);
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
}

export const ComplianceView: React.FC<{ userRole?: string }> = ({ userRole }) => {
  const role = (userRole ?? '').toUpperCase();
  const canView = roleMatches(role, VIEW_ROLES);
  const canWrite = WRITE_ROLES.includes(role);
  const isMobile = useIsMobile();

  const [rules, setRules] = useState<ContributionRule[]>([]);
  const [expenditures, setExpenditures] = useState<Expenditure[]>([]);
  const [totalCents, setTotalCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // rule form
  const [rOffice, setROffice] = useState('');
  const [rJurisdiction, setRJurisdiction] = useState('');
  const [rUnlimited, setRUnlimited] = useState(false);
  const [rLimit, setRLimit] = useState('');
  const [rBusy, setRBusy] = useState(false);

  // expenditure form
  const [ePayer, setEPayer] = useState('');
  const [eAmount, setEAmount] = useState('');
  const [eChannel, setEChannel] = useState('');
  const [eOffice, setEOffice] = useState('');
  const [eContest, setEContest] = useState('');
  const [eDisclaimer, setEDisclaimer] = useState('');
  const [eBusy, setEBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const disclaimerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rs, ex] = await Promise.all([listRules(), listExpenditures()]);
      setRules(rs);
      setExpenditures(ex.items);
      setTotalCents(ex.total_cents);
    } catch (err) {
      setError('Could not load compliance data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canView) void load();
  }, [canView, load]);

  // Live disclaimer preview as the payer is typed (debounced).
  useEffect(() => {
    if (!ePayer.trim()) {
      setEDisclaimer('');
      return;
    }
    if (disclaimerTimer.current) clearTimeout(disclaimerTimer.current);
    disclaimerTimer.current = setTimeout(() => {
      getDisclaimer(ePayer.trim())
        .then(setEDisclaimer)
        .catch(() => setEDisclaimer(`Paid for by ${ePayer.trim()}.`));
    }, 300);
    return () => {
      if (disclaimerTimer.current) clearTimeout(disclaimerTimer.current);
    };
  }, [ePayer]);

  const submitRule = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!rOffice.trim()) return;
    setRBusy(true);
    try {
      await upsertRule({
        office_type: rOffice.trim(),
        jurisdiction: rJurisdiction.trim() || null,
        limit_cents: rUnlimited ? null : dollarsToCents(rLimit),
      });
      setROffice('');
      setRJurisdiction('');
      setRLimit('');
      setRUnlimited(false);
      await load();
    } catch {
      setError('Could not save the rule.');
    } finally {
      setRBusy(false);
    }
  };

  const submitExpenditure = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ePayer.trim() || dollarsToCents(eAmount) <= 0) return;
    setEBusy(true);
    try {
      await createExpenditure({
        payer: ePayer.trim(),
        amount_cents: dollarsToCents(eAmount),
        channel: eChannel.trim() || null,
        office_type: eOffice.trim() || null,
        contest_label: eContest.trim() || null,
      });
      setEPayer('');
      setEAmount('');
      setEChannel('');
      setEOffice('');
      setEContest('');
      await load();
    } catch {
      setError('Could not record the expenditure.');
    } finally {
      setEBusy(false);
    }
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const blob = await exportExpendituresCsv();
      triggerBrowserDownload(blob, 'expenditures-filing.csv');
    } catch {
      setError('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  if (!canView) {
    return (
      <div className="p-6">
        <div className="max-w-lg mx-auto bg-bg-raised border border-border-base rounded-xl p-6 text-center">
          <ShieldAlert className="mx-auto mb-3 text-fg-muted" size={28} />
          <h2 className="text-lg font-bold text-fg-primary">Access restricted</h2>
          <p className="text-sm text-fg-muted mt-1">
            Compliance is limited to committee administrators.
          </p>
        </div>
      </div>
    );
  }

  const inputCls =
    'w-full rounded-lg border border-border-base bg-bg-base px-3 py-2 text-sm text-fg-primary';

  return (
    <div className="p-4 md:p-6 space-y-6">
      <ViewHeader
        title={
          <span className="flex items-center gap-2">
            <Scale className="w-6 h-6 text-accent" />
            Compliance
          </span>
        }
        tagline="Contribution limits, expenditures, and the filing export."
        actions={
          <button
            onClick={onExport}
            disabled={exporting || expenditures.length === 0}
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-hover disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm font-bold"
          >
            <Download size={16} /> {exporting ? 'Exporting…' : 'Export filing CSV'}
          </button>
        }
      />

      {error && (
        <div className="bg-bg-base border border-border-base text-fg-secondary rounded-xl p-3 text-sm">
          {error}
        </div>
      )}

      {/* Contribution limits */}
      <section className="space-y-3">
        <h2 className="text-lg font-bold text-fg-primary">Contribution limits</h2>
        <p className="text-xs text-fg-muted">
          Stored as data per office — no jurisdiction rules are hardcoded. NULL = no limit.
        </p>
        <div className="bg-bg-raised rounded-xl border border-border-base overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-base text-left text-fg-muted">
                <th className="py-2.5 px-4 font-semibold">Office</th>
                <th className="py-2.5 px-4 font-semibold">Jurisdiction</th>
                <th className="py-2.5 px-4 font-semibold text-right">Limit / election</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-3 px-4 text-fg-muted" colSpan={3}>
                    Loading…
                  </td>
                </tr>
              ) : rules.length === 0 ? (
                <tr>
                  <td className="py-3 px-4 text-fg-muted" colSpan={3}>
                    No rules yet.
                  </td>
                </tr>
              ) : (
                rules.map((r) => (
                  <tr key={r.id} className="border-b border-border-base last:border-0">
                    <td className="py-2.5 px-4 text-fg-primary">{r.office_type}</td>
                    <td className="py-2.5 px-4 text-fg-secondary">{r.jurisdiction ?? '—'}</td>
                    <td
                      className={`py-2.5 px-4 text-right tabular-nums ${
                        r.limit_cents == null ? 'text-fg-muted' : 'text-fg-secondary'
                      }`}
                    >
                      {money(r.limit_cents)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {canWrite && (
          <form
            onSubmit={submitRule}
            className="bg-bg-raised rounded-xl border border-border-base p-4 grid gap-3 md:grid-cols-4 items-end"
          >
            <label className="text-xs text-fg-muted md:col-span-1">
              Office
              <input
                className={inputCls}
                value={rOffice}
                onChange={(e) => setROffice(e.target.value)}
                placeholder="State Representative"
              />
            </label>
            <label className="text-xs text-fg-muted">
              Jurisdiction
              <input
                className={inputCls}
                value={rJurisdiction}
                onChange={(e) => setRJurisdiction(e.target.value)}
                placeholder="Missouri"
              />
            </label>
            <label className="text-xs text-fg-muted">
              Limit ($ / election)
              <input
                className={inputCls}
                value={rLimit}
                onChange={(e) => setRLimit(e.target.value)}
                disabled={rUnlimited}
                placeholder="2600"
                inputMode="decimal"
              />
            </label>
            <div className="flex items-center gap-3">
              <label className="text-xs text-fg-secondary inline-flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={rUnlimited}
                  onChange={(e) => setRUnlimited(e.target.checked)}
                />
                No limit
              </label>
              <button
                type="submit"
                disabled={rBusy || !rOffice.trim()}
                className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm font-bold"
              >
                {rBusy ? 'Saving…' : 'Save rule'}
              </button>
            </div>
          </form>
        )}
      </section>

      {/* Expenditures */}
      <section className="space-y-3">
        <div className="flex items-baseline justify-between gap-2">
          <h2 className="text-lg font-bold text-fg-primary">Expenditures</h2>
          <div className="text-sm text-fg-secondary tabular-nums">
            Total{' '}
            <span className="font-bold text-fg-primary">
              ${(totalCents / 100).toLocaleString('en-US')}
            </span>
          </div>
        </div>

        <div className="bg-bg-raised rounded-xl border border-border-base overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-base text-left text-fg-muted">
                <th className="py-2.5 px-4 font-semibold">Payer / disclaimer</th>
                {!isMobile && <th className="py-2.5 px-4 font-semibold">Channel</th>}
                {!isMobile && <th className="py-2.5 px-4 font-semibold">Office / contest</th>}
                <th className="py-2.5 px-4 font-semibold text-right">Amount</th>
                {!isMobile && <th className="py-2.5 px-4 font-semibold">Date</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td className="py-3 px-4 text-fg-muted" colSpan={5}>
                    Loading…
                  </td>
                </tr>
              ) : expenditures.length === 0 ? (
                <tr>
                  <td className="py-3 px-4 text-fg-muted" colSpan={5}>
                    No expenditures recorded.
                  </td>
                </tr>
              ) : (
                expenditures.map((x) => (
                  <tr key={x.id} className="border-b border-border-base last:border-0 align-top">
                    <td className="py-2.5 px-4">
                      <div className="text-fg-primary">{x.payer}</div>
                      <div className="text-xs text-fg-muted">{x.disclaimer}</div>
                    </td>
                    {!isMobile && (
                      <td className="py-2.5 px-4 text-fg-secondary">{x.channel ?? '—'}</td>
                    )}
                    {!isMobile && (
                      <td className="py-2.5 px-4 text-fg-secondary">
                        {[x.office_type, x.contest_label].filter(Boolean).join(' · ') || '—'}
                      </td>
                    )}
                    <td className="py-2.5 px-4 text-right tabular-nums text-fg-secondary">
                      ${(x.amount_cents / 100).toLocaleString('en-US')}
                    </td>
                    {!isMobile && (
                      <td className="py-2.5 px-4 text-fg-muted text-xs">
                        {(x.spent_at ?? x.created_at ?? '').slice(0, 10)}
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {canWrite && (
          <form
            onSubmit={submitExpenditure}
            className="bg-bg-raised rounded-xl border border-border-base p-4 space-y-3"
          >
            <div className="grid gap-3 md:grid-cols-5 items-end">
              <label className="text-xs text-fg-muted md:col-span-2">
                Payer
                <input
                  className={inputCls}
                  value={ePayer}
                  onChange={(e) => setEPayer(e.target.value)}
                  placeholder="Vote Real Republicans"
                />
              </label>
              <label className="text-xs text-fg-muted">
                Amount ($)
                <input
                  className={inputCls}
                  value={eAmount}
                  onChange={(e) => setEAmount(e.target.value)}
                  placeholder="3300"
                  inputMode="decimal"
                />
              </label>
              <label className="text-xs text-fg-muted">
                Channel
                <input
                  className={inputCls}
                  value={eChannel}
                  onChange={(e) => setEChannel(e.target.value)}
                  placeholder="mail"
                />
              </label>
              <label className="text-xs text-fg-muted">
                Office
                <input
                  className={inputCls}
                  value={eOffice}
                  onChange={(e) => setEOffice(e.target.value)}
                  placeholder="State Representative"
                />
              </label>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="text-xs text-fg-muted">
                Disclaimer:{' '}
                <span className="text-fg-secondary">
                  {eDisclaimer || 'Paid for by …'}
                </span>{' '}
                <span className="italic">(auto-generated)</span>
              </div>
              <button
                type="submit"
                disabled={eBusy || !ePayer.trim() || dollarsToCents(eAmount) <= 0}
                className="bg-accent hover:bg-accent-hover disabled:opacity-50 text-white px-3 py-2 rounded-lg text-sm font-bold"
              >
                {eBusy ? 'Recording…' : 'Record expenditure'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
};

export default ComplianceView;
